import * as React from "react"
import { Col, Hidden, Row, Visible } from "react-grid-system"
import Card from "../../atoms/Card"
import H3 from "../../atoms/typography/H3"
import Subtitle from "../../atoms/typography/Subtitle"
import PrimaryButton from "../../atoms/buttons/PrimaryButton"
import { EditIcon } from "../../atoms/icons/EditIcon"
import Checkbox from "../../atoms/inputs/Checkbox"
import AddressForm, {
  DEFAULT_BILLING_ADDRESS_INFO,
  DEFAULT_SHIPPING_ADDRESS_INFO,
  isShippingAndBillingSameAddress,
  updateAddress,
} from "../../molecules/AddressForm"
import MetricChip from "../../molecules/MetricChip"
import { useMyOrganization } from "../../../hooks/useMyOrganization"
import { HorizontalSpacer, Spacer } from "../../atoms/spacers"
import { theme } from "../../theme"
import { useUpdateOrgAddress } from "../../../hooks/useUpdateOrgAddress"
import { useUpdateOrgShippingAddress } from "../../../hooks/useUpdateOrgShippingAddress"
import { PersonIcon } from "../../atoms/icons/PersonIcon"
import { GroupsIcons } from "../../atoms/icons/GroupsIcon"
import { AddTerminalIcon } from "../../atoms/icons/AddTerminalIcon"
import { RssFeedIcon } from "../../atoms/icons/RssFeedIcon"
import { AccountTreeIcon } from "../../atoms/icons/AccountTreeIcon"
import styled from "styled-components"
import { MediaQuery } from "../../../utils/design-helpers"
import SecondaryButton from "../../atoms/buttons/SecondaryButton"
import { BillingAddress, ShippingAddress } from "../../../utils/models"
import H4 from "../../atoms/typography/H4"
import { ApolloError } from "@apollo/client"
import { captureExceptionWithContext } from "../../../utils/sentry"
import Body from "../../atoms/typography/Body"

const MetricChipsContainer = styled(Col)`
  display: flex;
  justify-content: space-around;
  align-items: center;

  ${MediaQuery.XS} {
    flex-direction: column;
  }
`

const DetailsPage: React.FunctionComponent = () => {
  const { myOrganization, orgLoading } = useMyOrganization()

  const {
    members: users,
    groups,
    terminals,
    subOrganizations: suborgs,
    activeDataPlans: dataPlans,
  } = myOrganization?.stats!

  const [billingAddress, setBillingAddress] = React.useState<BillingAddress>(DEFAULT_BILLING_ADDRESS_INFO)
  const [shippingAddress, setShippingAddress] = React.useState<ShippingAddress>(DEFAULT_BILLING_ADDRESS_INFO)

  const [isEditingBilling, setIsEditingBilling] = React.useState(false)
  const [isEditingShipping, setIsEditingShipping] = React.useState(false)

  const [hasMadeChangesToBilling, setHasMadeChangesToBilling] = React.useState(false)
  const [hasMadeChangesToShipping, setHasMadeChangesToShipping] = React.useState(false)

  const [useBillingForShipping, setUseBillingForShipping] = React.useState(false)

  const [shippingAddressErrors, setShippingAddressErrors] = React.useState<string[]>([])
  const [billingAddressErrors, setBillingAddressErrors] = React.useState<string[]>([])

  // Update fields with values when organization has loaded
  React.useEffect(() => {
    if (myOrganization) {
      setBillingAddress(updateAddress(DEFAULT_BILLING_ADDRESS_INFO, myOrganization.billingAddress))
      setShippingAddress(updateAddress(DEFAULT_BILLING_ADDRESS_INFO, myOrganization.shippingAddress))

      if (isShippingAndBillingSameAddress(myOrganization.shippingAddress, myOrganization.billingAddress)) {
        setUseBillingForShipping(true)
      }
    }
  }, [myOrganization, myOrganization?.billingAddress, myOrganization?.shippingAddress])

  const onChangeBillingAddress = (event: any) => {
    const name = event.target.name
    const value = event.target.value

    setBillingAddress((values) => ({ ...values, [name]: value }))
    setHasMadeChangesToBilling(true)
  }

  const onChangeShippingAddress = (event: any) => {
    const name = event.target.name
    const value = event.target.value

    setShippingAddress((values) => ({ ...values, [name]: value }))
    setHasMadeChangesToShipping(true)
  }

  const onUpdateOrgBillingAddress = async (address: BillingAddress) => {
    try {
      await updateOrgBillingAddress({
        variables: {
          input: {
            id: myOrganization?.id,
            ...updateAddress(DEFAULT_BILLING_ADDRESS_INFO, address),
          },
        },
      })
    } catch (error) {
      captureExceptionWithContext(error, "error updating billing address")
    }
  }

  // dispatches mutation that updates the shipping address
  const onUpdateOrgShippingAddress = async (address: ShippingAddress) => {
    try {
      await updateOrgShippingAddress({
        variables: {
          input: updateAddress(DEFAULT_SHIPPING_ADDRESS_INFO, address),
        },
      })
    } catch (error) {
      captureExceptionWithContext(error, "error updating shipping address")
    }
  }

  const onCompletedUpdateAddress = (updateShippingData?: any, updateBillingData?: any) => {
    setShippingAddressErrors(updateShippingData?.updateShippingAddress?.errors[0]?.messages ?? [])
    setBillingAddressErrors(updateBillingData?.updateOrganization?.errors[0]?.messages ?? [])
    if (
      updateShippingData?.updateShippingAddress?.errors?.length < 1 ||
      updateBillingData?.updateOrganization.errors?.length < 1
    ) {
      setIsEditingShipping(false)
      setIsEditingBilling(false)
      setHasMadeChangesToBilling(false)
      setHasMadeChangesToShipping(false)

      setUseBillingForShipping(isShippingAndBillingSameAddress(shippingAddress, billingAddress))
    }
  }

  const onErrorUpdateAddress = (apolloError: ApolloError) => {
    captureExceptionWithContext(apolloError, "error updating organization address")
  }

  const { updateOrgAddress: updateOrgBillingAddress } = useUpdateOrgAddress({
    onCompleted: (data: any) => onCompletedUpdateAddress(undefined, data),
    onError: onErrorUpdateAddress,
  })

  const { updateOrgShippingAddress } = useUpdateOrgShippingAddress({
    onCompleted: (data: any) => onCompletedUpdateAddress(data, undefined),
    onError: onErrorUpdateAddress,
  })

  // called when submitting the billing address form
  const onSubmitBillingAddress = (event: React.FormEvent) => {
    event.preventDefault()
    onUpdateOrgBillingAddress(billingAddress)
  }

  // called when submitting the shipping address form
  const onSubmitShippingAddress = (event: React.FormEvent) => {
    event.preventDefault()
    onUpdateOrgShippingAddress(shippingAddress)
  }

  // called when clicking the "same as shipping address" box
  const onSetUseBillingForShipping = (event: React.MouseEvent) => {
    event.preventDefault()
    if (useBillingForShipping) {
      setUseBillingForShipping(false)
    } else {
      onUpdateOrgShippingAddress(billingAddress)
    }
  }

  const onCancelEditingBillingAddress = () => {
    setIsEditingBilling(false)
    setHasMadeChangesToBilling(false)

    if (myOrganization?.billingAddress) {
      setBillingAddress(myOrganization?.billingAddress)
      setUseBillingForShipping(isShippingAndBillingSameAddress(shippingAddress, myOrganization?.billingAddress))
    }
  }

  const onCancelEditingShippingAddress = () => {
    setIsEditingShipping(false)
    setHasMadeChangesToShipping(false)

    if (myOrganization?.shippingAddress) {
      setShippingAddress(myOrganization?.shippingAddress)
      setUseBillingForShipping(isShippingAndBillingSameAddress(myOrganization?.shippingAddress, billingAddress))
    }
  }

  const Metrics = () => (
    <div>
      <Row justify="center">
        <Subtitle>Metrics</Subtitle>
      </Row>
      <HorizontalSpacer spacing={2} />
      <Row justify="center">
        <Hidden xs md>
          <MetricChipsContainer md={8}>
            <MetricChip icon={<PersonIcon />} value={`${users} Members`} />
            <MetricChip icon={<GroupsIcons />} value={`${groups} Groups`} />
            <MetricChip icon={<AddTerminalIcon />} value={`${terminals} Terminals`} />
            <MetricChip icon={<RssFeedIcon />} value={`${dataPlans} Data plans`} />
            <MetricChip icon={<AccountTreeIcon />} value={`${suborgs} Suborganization`} />
          </MetricChipsContainer>
        </Hidden>
        <Visible xs md>
          <Col md={6} xs={5} style={{ padding: 2 }}>
            <MetricChip icon={<PersonIcon />} value={`${users} Members`} />
          </Col>
          <Col md={6} xs={5} style={{ padding: 2 }}>
            <MetricChip icon={<GroupsIcons />} value={`${groups} Groups`} />
          </Col>
          <Col md={6} xs={5} style={{ padding: 2 }}>
            <MetricChip icon={<AddTerminalIcon />} value={`${terminals} Terminals`} />
          </Col>
          <Col md={6} xs={5} style={{ padding: 2 }}>
            <MetricChip icon={<RssFeedIcon />} value={`${dataPlans} Data plans`} />
          </Col>
          <Col md={6} xs={8} style={{ padding: 2 }}>
            <MetricChip icon={<AccountTreeIcon />} value={`${suborgs} Suborganization`} />
          </Col>
        </Visible>
      </Row>
    </div>
  )

  return (
    <div>
      <Card>
        <HorizontalSpacer spacing={2} />
        <Metrics />
        <HorizontalSpacer spacing={2} />

        <Row justify="between" style={{ padding: "1rem" }}>
          <Col lg={5} md={12}>
            <form onSubmit={onSubmitBillingAddress}>
              <Row justify="between" align="center" style={{ padding: theme.spacings.large }}>
                <H3>Billing address</H3>
                {!isEditingBilling && (
                  <PrimaryButton title="Edit" icon={<EditIcon />} onClick={() => setIsEditingBilling(true)} />
                )}
                {isEditingBilling && (
                  <div style={{ display: "flex" }}>
                    <SecondaryButton title="Save" type="submit" disabled={!hasMadeChangesToBilling} />
                    <Spacer spacing={1} />
                    <PrimaryButton title="Cancel" onClick={onCancelEditingBillingAddress} />
                  </div>
                )}
              </Row>
              <HorizontalSpacer spacing={1} />
              {!orgLoading && (
                <AddressForm
                  address1Value={billingAddress.address1}
                  address2Value={billingAddress.address2}
                  cityValue={billingAddress.city}
                  zipCodeValue={billingAddress.zipCode}
                  countryValue={billingAddress.country}
                  vatValue={billingAddress.vat}
                  onChange={onChangeBillingAddress}
                  disabled={!isEditingBilling}
                />
              )}
            </form>

            {billingAddressErrors && (
              <div>
                <HorizontalSpacer spacing={1} />
                <H3 color={theme.colors.red}>{billingAddressErrors}</H3>
                <HorizontalSpacer spacing={1} />
              </div>
            )}
          </Col>

          <Col lg={5} md={12}>
            <form onSubmit={onSubmitShippingAddress}>
              <Row align="center" justify="between" style={{ padding: theme.spacings.large }}>
                <div>
                  <Visible xs>
                    <HorizontalSpacer spacing={4} />
                  </Visible>
                  <H3>Personal shipping address</H3>
                  <Visible xs>
                    <HorizontalSpacer spacing={2} />
                  </Visible>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <Checkbox
                      checked={useBillingForShipping}
                      onChange={onSetUseBillingForShipping}
                      disabled={isEditingShipping || isEditingBilling}
                    />
                    <Spacer spacing={1} />
                    <Body>Same as billing address</Body>
                  </div>
                  <Visible xs>
                    <HorizontalSpacer spacing={2} />
                  </Visible>
                </div>

                <Spacer spacing={2} />

                {!isEditingShipping && !useBillingForShipping && (
                  <PrimaryButton title="Edit" icon={<EditIcon />} onClick={() => setIsEditingShipping(true)} />
                )}
                {isEditingShipping && (
                  <div style={{ display: "flex" }}>
                    <SecondaryButton title="Save" type="submit" disabled={!hasMadeChangesToShipping} />
                    <Spacer spacing={1} />
                    <PrimaryButton title="Cancel" onClick={onCancelEditingShippingAddress} />
                  </div>
                )}
              </Row>
              <HorizontalSpacer spacing={1} />
              {!orgLoading && (
                <div>
                  <AddressForm
                    address1Value={shippingAddress.address1}
                    address2Value={shippingAddress.address2}
                    cityValue={shippingAddress.city}
                    zipCodeValue={shippingAddress.zipCode}
                    countryValue={shippingAddress.country}
                    onChange={onChangeShippingAddress}
                    disabled={!isEditingShipping}
                    hideVat
                  />
                </div>
              )}
            </form>

            {shippingAddressErrors && (
              <div>
                <HorizontalSpacer spacing={1} />
                <H3 color={theme.colors.red}>{shippingAddressErrors}</H3>
                <HorizontalSpacer spacing={1} />
              </div>
            )}
          </Col>
        </Row>
        <HorizontalSpacer spacing={2} />
      </Card>
      <HorizontalSpacer spacing={3} />
      <H4>What is Organization?</H4>
      <HorizontalSpacer spacing={1} />
      <Body>
        ‘Organization’ refers to the account associated with you and your workplace. By having an ‘Organization’ you can
        gather all your terminals in one place, keep track of data usage, data plans, as well as who your users are.
      </Body>
      <HorizontalSpacer spacing={1} />
      <Body>
        With Organization, you can:
        <HorizontalSpacer spacing={0.5} />
        ・Manage and organize terminals
        <HorizontalSpacer spacing={0.5} />
        ・View terminal statistics and data consumptions of your terminals and plans
        <HorizontalSpacer spacing={0.5} />
        ・Complete purchases in the store
        <HorizontalSpacer spacing={0.5} />
        ・Manage your organization users by creating sub-organizations and groups
        <HorizontalSpacer spacing={0.5} />
        ・Manage who has access to what terminals with groups
      </Body>
      <HorizontalSpacer spacing={1} />
      <Body>
        If your company already has an Organization, ask your administrator to invite you. If you are the administrator,
        you can create a new Organization below.
        <HorizontalSpacer spacing={1} />
        The organization structure allows your organization to make all your purchases with the same billing method and
        gather your receipts in one place. All activity in your organization can be kept track of through the activity
        log, to verify which users have made which changes.
        <HorizontalSpacer spacing={1} />
        The organization and the members can be managed precisely through groups and sub-organizations, allowing
        tailored viewing permissions and terminal access according to your needs.
      </Body>
      <HorizontalSpacer spacing={3} />
    </div>
  )
}

export default DetailsPage
